import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useBoard, useMembers, useTags } from '../lib/queries'
import { filterQuery, parseFilters, type Filters } from '../lib/filters'
import { useProjectEvents } from '../lib/useSSE'
import type { Task, User } from '../lib/types'
import { Board } from '../components/Board'
import { ActiveFilterChips, FilterBar } from '../components/FilterBar'
import { ExportButton } from '../components/ExportButton'
import { TaskDrawer } from '../components/TaskDrawer'
import { TaskList } from '../components/TaskList'
import { Button, EmptyState, Spinner, cx } from '../components/ui'

export function BoardPage({ user }: { user: User }) {
  const { key = '', taskKey } = useParams()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()

  const filters = useMemo(() => parseFilters(params), [params])
  const query = useMemo(() => filterQuery(filters), [filters])
  const view = params.get('view') === 'list' ? 'list' : 'board'

  // Done columns arrive capped so a board with hundreds of finished cards still
  // opens instantly. Lifting the cap is a refetch, not a client-side slice —
  // the extra cards were never sent in the first place.
  const [showAllDone, setShowAllDone] = useState(false)

  // A parent board shows its sub-projects by default, because seeing
  // everything underneath is the reason to open it. The narrow view lives in
  // the URL so it survives a reload and can be linked.
  const onlyThisProject = params.get('only') === '1'

  const boardQuery = useMemo(() => {
    const extra: string[] = []
    if (showAllDone) extra.push('donePerColumn=0')
    if (onlyThisProject) extra.push('onlyThisProject=true')
    if (extra.length === 0) return query
    return `${query || '?'}${query ? '&' : ''}${extra.join('&')}`
  }, [query, showAllDone, onlyThisProject])

  const project = useQuery({
    queryKey: ['project-by-key', key],
    queryFn: () => api.projectByKey(key),
    enabled: Boolean(key),
    retry: false,
  })

  const projectId = project.data?.id
  const board = useBoard(projectId, boardQuery)
  const members = useMembers(projectId)
  const tags = useTags()
  const { connected, recentlyTouched } = useProjectEvents(projectId, user.id)

  // Deep links use the human-readable key (/p/SHOP/t/SHOP-142), so the drawer
  // resolves it to an id rather than the URL carrying a UUID nobody can read.
  const linked = useQuery({
    queryKey: ['task-by-key', taskKey],
    queryFn: () => api.taskByKey(taskKey!),
    enabled: Boolean(taskKey),
    retry: false,
  })

  const setFilters = useCallback(
    (next: Filters) => {
      const nextParams = new URLSearchParams(filterQuery(next).replace(/^\?/, ''))
      if (view === 'list') nextParams.set('view', 'list')
      setParams(nextParams, { replace: true })
    },
    [setParams, view],
  )

  const setOnly = useCallback(
    (only: boolean) => {
      const next = new URLSearchParams(params)
      if (only) next.set('only', '1')
      else next.delete('only')
      setParams(next, { replace: true })
    },
    [params, setParams],
  )

  const openTask = useCallback(
    (task: Task) => navigate(`/p/${key}/t/${task.key}${window.location.search}`),
    [navigate, key],
  )
  const closeDrawer = useCallback(
    () => navigate(`/p/${key}${window.location.search}`),
    [navigate, key],
  )

  // Selection lives here because both the board and the bar above it need it.
  // It survives a bulk drop on purpose, so several columns can be worked
  // through without re-picking the same cards.
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])
  const clearSelection = useCallback(() => setSelected(new Set()), [])

  // Changing board is a change of subject; carrying a selection across would
  // export tasks you can no longer see.
  useEffect(() => {
    setSelected(new Set())
  }, [key])

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') clearSelection()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [clearSelection])

  // `c` creates a task in the first column, matching the quick-add affordance.
  const [pendingCreate, setPendingCreate] = useState(false)
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const typing =
        e.target instanceof HTMLElement &&
        (e.target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName))
      if (e.key === 'c' && !typing && !e.metaKey && !e.ctrlKey && !taskKey) {
        e.preventDefault()
        setPendingCreate(true)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [taskKey])

  if (project.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Spinner className="h-5 w-5 text-[var(--ink-3)]" />
      </div>
    )
  }

  if (project.isError || !project.data) {
    return (
      <EmptyState
        title="This board is not available"
        hint="It may have been deleted, or you are not a member of it."
        action={
          <Link to="/my">
            <Button>Go to my tasks</Button>
          </Link>
        }
      />
    )
  }

  const canEdit = project.data.myRole !== ''

  return (
    <>
      {/* Wraps rather than truncates: on a phone the row is too long for one
          line, and the thing that fell off the end was the Settings button. */}
      <header className="flex flex-wrap items-center gap-x-2.5 gap-y-2 px-4 py-3">
        <span
          aria-hidden
          className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
          style={{ background: project.data.color }}
        />
        {project.data.parentKey && (
          <Link
            to={`/p/${project.data.parentKey}`}
            className="shrink-0 text-[13px] text-[var(--ink-3)] transition-colors duration-[140ms] hover:text-[var(--ink)]"
          >
            {project.data.parentName} <span aria-hidden>/</span>
          </Link>
        )}
        <h1 className="truncate text-[15px] font-semibold tracking-tight">{project.data.name}</h1>
        {/* The name is what identifies the board; the key is a convenience.
            Narrow screens drop the key rather than squeezing out the name. */}
        <span className="task-key hidden shrink-0 text-[12px] text-[var(--ink-3)] sm:inline">
          {project.data.key}
        </span>

        {project.data.childCount > 0 && (
          <div className="flex shrink-0 rounded-md border border-[var(--line)] p-0.5">
            <ViewTab active={!onlyThisProject} onClick={() => setOnly(false)}>
              Everything
            </ViewTab>
            <ViewTab active={onlyThisProject} onClick={() => setOnly(true)}>
              {project.data.name} only
            </ViewTab>
          </div>
        )}

        {/* A quiet dot: live sync is either working or it is not, and people
            should be able to tell without being told. */}
        <span
          title={connected ? 'Live updates on' : 'Reconnecting…'}
          className={cx(
            'h-1.5 w-1.5 shrink-0 rounded-full transition-colors duration-[400ms]',
            connected ? 'bg-[var(--good)]' : 'bg-[var(--ink-3)]',
          )}
        />

        <div className="ml-auto flex items-center gap-1.5">
          <div className="flex rounded-md border border-[var(--line)] p-0.5">
            <ViewTab active={view === 'board'} onClick={() => switchView(params, setParams, 'board')}>
              Board
            </ViewTab>
            <ViewTab active={view === 'list'} onClick={() => switchView(params, setParams, 'list')}>
              List
            </ViewTab>
          </div>

          {project.data.myRole === 'owner' && (
            <Link to={`/p/${key}/settings`}>
              <Button size="sm">Settings</Button>
            </Link>
          )}
        </div>
      </header>

      <FilterBar
        filters={filters}
        onChange={setFilters}
        members={members.data ?? []}
        tags={tags.data ?? []}
        currentUser={user}
      />

      <ActiveFilterChips
        filters={filters}
        onChange={setFilters}
        members={members.data ?? []}
        tags={tags.data ?? []}
      />

      {selected.size > 0 && (
        <SelectionBar
          selected={selected}
          visible={board.data?.tasks ?? []}
          onSelectAll={() => setSelected(new Set((board.data?.tasks ?? []).map((t) => t.id)))}
          onClear={clearSelection}
        />
      )}

      {board.isLoading && !board.data ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner className="h-5 w-5 text-[var(--ink-3)]" />
        </div>
      ) : !board.data ? (
        <EmptyState title="Could not load this board" hint="Try reloading the page." />
      ) : view === 'list' ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <TaskList
            tasks={board.data.tasks}
            onOpen={openTask}
            showProject={Boolean(board.data.aggregated)}
          />
        </div>
      ) : (
        <div className="group/board min-h-0 flex-1 pt-1">
          <Board
            statuses={board.data.statuses}
            tasks={board.data.tasks}
            counts={board.data.counts ?? {}}
            projectId={project.data.id}
            aggregated={Boolean(board.data.aggregated)}
            user={user}
            defaultAssignee={project.data.defaultAssignee}
            onOpenTask={openTask}
            pulsing={recentlyTouched}
            canEdit={canEdit}
            onShowAllDone={() => setShowAllDone(true)}
            selected={selected}
            onToggleSelect={toggleSelect}
          />
        </div>
      )}

      {taskKey && linked.data && (
        <TaskDrawer
          taskId={linked.data.id}
          statuses={board.data?.statuses ?? []}
          members={members.data ?? []}
          user={user}
          onClose={closeDrawer}
        />
      )}

      {pendingCreate && board.data && (
        <QuickCreate
          projectId={project.data.id}
          statusId={board.data.statuses[0]?.id}
          onDone={() => setPendingCreate(false)}
        />
      )}
    </>
  )
}

/**
 * Appears only once something is selected.
 *
 * Export lives here rather than in the toolbar because an export is always of
 * a specific set of tasks — a button that exports "whatever is on screen" is
 * one you have to check the filters before trusting.
 */
function SelectionBar({
  selected, visible, onSelectAll, onClear,
}: {
  selected: Set<string>
  visible: Task[]
  onSelectAll: () => void
  onClear: () => void
}) {
  const allVisibleSelected = visible.length > 0 && visible.every((t) => selected.has(t.id))

  return (
    <div className="fade-in flex flex-wrap items-center gap-2 border-b border-[var(--line)] bg-[var(--accent-soft)] px-4 py-2">
      <span className="text-[13px] font-medium text-[var(--accent)]">
        {selected.size} selected
      </span>

      <span className="text-[12px] text-[var(--ink-3)]">
        Drag any one of them to move the whole set.
      </span>

      {!allVisibleSelected && visible.length > 0 && (
        <Button size="sm" variant="ghost" onClick={onSelectAll}>
          Select all {visible.length}
        </Button>
      )}

      <div className="ml-auto flex items-center gap-1.5">
        <ExportButton taskIds={[...selected]} size="sm" variant="primary" />
        <Button size="sm" variant="ghost" onClick={onClear}>
          Clear
        </Button>
      </div>
    </div>
  )
}

function switchView(
  params: URLSearchParams,
  setParams: (next: URLSearchParams, opts?: { replace?: boolean }) => void,
  view: 'board' | 'list',
) {
  const next = new URLSearchParams(params)
  if (view === 'list') next.set('view', 'list')
  else next.delete('view')
  setParams(next, { replace: true })
}

function ViewTab({
  active, onClick, children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        'rounded px-2 py-0.5 text-[12px] transition-colors duration-[140ms]',
        active
          ? 'bg-[var(--sunken)] font-medium text-[var(--ink)]'
          : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]',
      )}
    >
      {children}
    </button>
  )
}

/** The `c` shortcut: a bare title box that creates and gets out of the way. */
function QuickCreate({
  projectId, statusId, onDone,
}: {
  projectId: string
  statusId: string | undefined
  onDone: () => void
}) {
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const navigate = useNavigate()

  async function submit() {
    const value = title.trim()
    if (!value) return onDone()
    setBusy(true)
    try {
      const task = await api.createTask({ projectId, title: value, statusId })
      onDone()
      navigate(`/p/${task.projectKey}/t/${task.key}`)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 pt-[18vh]" onClick={onDone}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="fade-in w-[calc(100vw-2rem)] max-w-[460px] rounded-lg border border-[var(--line)] bg-[var(--surface)] p-3 shadow-2xl shadow-black/20"
      >
        <input
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit()
            if (e.key === 'Escape') onDone()
          }}
          disabled={busy}
          placeholder="New task…"
          className="w-full bg-transparent text-[15px] outline-none placeholder:text-[var(--ink-3)]"
        />
        <p className="mt-2 flex items-center gap-2 text-[11px] text-[var(--ink-3)]">
          <kbd className="task-key rounded border border-[var(--line)] px-1">↵</kbd> create and open
          <kbd className="task-key ml-1 rounded border border-[var(--line)] px-1">esc</kbd> cancel
        </p>
      </div>
    </div>
  )
}
